import React, { useLayoutEffect, useMemo } from 'react';
import {
  useRecoilCallback,
  useRecoilState,
  useRecoilTransaction_UNSTABLE,
  useRecoilValue,
  useSetRecoilState,
} from 'recoil';
import {
  inputFormValueState,
  inputFormChangedState,
  formState,
  inputFormState,
} from '../../stores/formState';
import { completedComponentState } from '../../stores/completedComponentState';
import { scenarioState } from '../../stores/scenarioState';
import { questionIdState } from '../../stores/query';

interface Option {
  label: string;
  questionId: number;
}

export interface CheckBoxProps {
  componentId: string;
  options: Option[];
}

const MedicalQuestionnaireSelect: React.FC<CheckBoxProps> = props => {
  const [value, setValue] = useRecoilState(inputFormValueState(props.componentId));
  const setCompleted = useSetRecoilState(completedComponentState(props.componentId));
  const changed = useRecoilValue(inputFormChangedState(props.componentId));
  const setQuestionId = useSetRecoilState(questionIdState);
  const scenario = useRecoilValue(scenarioState);
  const process = useRecoilTransaction_UNSTABLE(({set}) => (questionId: number) => {
    set(formState, {[props.componentId]: questionId});
    const index = scenario.items.findIndex(i => i.components.map(c => c.id).some(cid => cid === props.componentId));
    if (index < 0) return;

    const items = scenario.items.filter((_, i) => i > index);
    items.forEach(i => {
      set(inputFormState(i.id), {});
    });
  }, [props.componentId, scenario]);

  const toggle = useRecoilCallback(({snapshot}) => {
    return async (option: Option) => {
      const questionId = await snapshot.getPromise(inputFormValueState(props.componentId))
      if (questionId === option.questionId) return;
      process(option.questionId)
      setValue(option.questionId);
      setQuestionId(option.questionId);
    }
  }, [props.componentId, process, setValue, setQuestionId]);

  const toggleOptions = useMemo(() => {
    return props.options.reduce((ret, option) => {
      ret[option.questionId] = () => {
        toggle(option)
      }
      return ret;
    }, {} as Record<string, () => void>);
  }, [props.options, toggle]);

  useLayoutEffect(() => {
    if (scenario.items[scenario.items.length - 1].components.some(c => c.id === props.componentId)) return;
    setCompleted(true);
  }, [props.componentId, scenario, setCompleted]);

  return (
    <div className={"select" + (changed ? " changed" : "")}>
      {props.options.map(option =>
        <button key={option.questionId}
                className={value === option.questionId ? 'checked' : ''}
                type="button"
                onClick={toggleOptions[option.questionId]}
        >{option.label}</button>
      )}
    </div>
  );
};

export default MedicalQuestionnaireSelect;
